'use client';
import React, { useMemo } from 'react';
import { generateSounding } from '@/lib/mockWeatherData';
import type { SoundingLevel } from '@/lib/mockWeatherData';
import { analyzeEmagramHour } from '@/lib/emagramAnalysis';

interface EmagramChartProps {
  hour: number;
  showDalr: boolean;
  showDewpoint: boolean;
  showThermalTop: boolean;
  soundingData?: SoundingLevel[] | null;
}

// â”€â”€â”€ Diagram constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const W = 900;
const H = 680;
const MARGIN = { top: 26, right: 150, bottom: 60, left: 70 };
const PLOT_W = W - MARGIN.left - MARGIN.right;
const PLOT_H = H - MARGIN.top - MARGIN.bottom;

// Pressure range (mb / hPa)
const P_TOP = 200;
const P_BOT = 1013;

// Temperature range (Â°C) at 1000 hPa baseline
const T_MIN = -40;
const T_MAX = 40;

// Skew factor: how many Â°C to shift right per pressure decade
const SKEW = 30; // Â°C shift from bottom to top

// â”€â”€â”€ Coordinate helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function pToY(p: number): number {
  // Log-pressure axis: high pressure / low altitude at the bottom,
  // low pressure / high altitude at the top.
  const logBot = Math.log(P_BOT);
  const logTop = Math.log(P_TOP);
  return MARGIN.top + PLOT_H * (Math.log(p) - logTop) / (logBot - logTop);
}

function tToX(t: number, p: number): number {
  // Skew: temperature axis tilts to the right as pressure decreases
  const skewOffset = SKEW * (Math.log(P_BOT) - Math.log(p)) / (Math.log(P_BOT) - Math.log(P_TOP));
  const tRange = T_MAX - T_MIN;
  return MARGIN.left + PLOT_W * (t + skewOffset - T_MIN) / tRange;
}

// Pressure â†’ approximate altitude (m) using standard atmosphere
function pToAlt(p: number): number {
  return Math.round(44330 * (1 - Math.pow(p / 1013.25, 0.1903)));
}

// â”€â”€â”€ Thermodynamic lines â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

// Dry adiabats: Î¸ = T * (1000/p)^0.286  â†’ T = Î¸ * (p/1000)^0.286
function dryAdiabatPoints(theta: number): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  for (let p = P_BOT; p >= P_TOP; p -= 10) {
    let t = theta * Math.pow(p / 1000, 0.286) - 273.15;
    const x = tToX(t, p);
    const y = pToY(p);
    if (x >= MARGIN.left - 5 && x <= MARGIN.left + PLOT_W + 5) {
      pts.push({ x, y });
    }
  }
  return pts;
}

// Saturated adiabats (simplified moist lapse rate ~6Â°C/km)
function saturatedAdiabatPoints(tSurface: number): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  let t = tSurface;
  const pStep = 10;
  for (let p = P_BOT; p >= P_TOP; p -= pStep) {
    const x = tToX(t, p);
    const y = pToY(p);
    if (x >= MARGIN.left - 5 && x <= MARGIN.left + PLOT_W + 5) {
      pts.push({ x, y });
    }
    // Moist lapse rate: ~6Â°C/km, convert to per mb step
    const dz = 29.3 * t / p * pStep; // approx dz in meters
    t -= 0.006 * dz;
  }
  return pts;
}

// Specific humidity lines (mixing ratio g/kg) â€” nearly vertical, slight tilt
function humidityLinePoints(w: number): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  for (let p = P_BOT; p >= 400; p -= 20) {
    // Approximate dew point from mixing ratio: Td â‰ˆ (243.5 * ln(e/6.112)) / (17.67 - ln(e/6.112))
    const e = w * p / (622 + w);
    const td = (243.5 * Math.log(e / 6.112)) / (17.67 - Math.log(e / 6.112));
    const x = tToX(td, p);
    const y = pToY(p);
    if (x >= MARGIN.left - 5 && x <= MARGIN.left + PLOT_W + 5) {
      pts.push({ x, y });
    }
  }
  return pts;
}

function pointsToPath(pts: { x: number; y: number }[]): string {
  if (pts.length === 0) return '';
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
}

// â”€â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export default function EmagramChart({ hour, showDalr, showDewpoint, showThermalTop, soundingData }: EmagramChartProps) {
  const sounding = useMemo(() => {
    if (soundingData && soundingData.length > 0) return soundingData;
    // Only fall back to mock data when soundingData is undefined (initial state),
    // not when it is explicitly null (location change in progress)
    if (soundingData === undefined) return generateSounding(hour);
    return null;
  }, [soundingData, hour]);

  // If sounding is null (explicit null passed = location change / no data yet), show placeholder
  if (!sounding) {
    return (
      <div
        className="flex items-center justify-center rounded-xl"
        style={{ width: '100%', minHeight: 620, backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)' }}
      >
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucune donnÃ©e de sondage disponible pour cette heure.</p>
      </div>
    );
  }

  // â”€â”€ Derived values â”€â”€
  const surfaceTemp = sounding[0].temp;
  const surfaceDew = sounding[0].dewpoint;
  const surfaceAlt = sounding[0].altitude; // actual altitude of surface level (m)
  const analysis = analyzeEmagramHour(sounding);

  // LCL altitude (simplified: 125m per Â°C of T-Td spread)
  const tdSpread = surfaceTemp - surfaceDew;
  const lclAlt = analysis.lclAGL;
  const lclPressure = analysis.lclPressure;

  // Thermal top: find the HIGHEST level where the dry adiabat parcel is still warmer than the environment.
  // The parcel starts at surfaceTemp at surfaceAlt and cools at DALR = 0.98Â°C/100m (9.8Â°C/km).
  // We scan ALL levels above surface and keep the last one where parcel temp > env temp.
  // This correctly handles cases where a surface inversion exists but the parcel breaks through it.
  let thermalTopIdx = 0; // default: surface (no buoyancy at all)
  for (let i = 1; i < sounding.length; i++) {
    const altAboveSurface = sounding[i].altitude - surfaceAlt;
    if (altAboveSurface <= 0) continue; // skip levels at or below surface
    const dalrTemp = surfaceTemp - (altAboveSurface / 100) * 0.98; // 9.8Â°C/km DALR
    if (dalrTemp > sounding[i].temp) {
      // Parcel is still buoyant at this level â€” keep updating the top
      thermalTopIdx = i;
    }
  }

  // Interpolate more precisely between thermalTopIdx and thermalTopIdx+1
  let thermalTopAlt = 0;
  let thermalTopP = sounding[0]?.pressure ?? 1013;
  if (thermalTopIdx > 0) {
    const lvlA = sounding[thermalTopIdx];
    const lvlB = sounding[thermalTopIdx + 1] ?? null;
    if (lvlB) {
      const altA = lvlA.altitude - surfaceAlt;
      const altB = lvlB.altitude - surfaceAlt;
      const parcelA = surfaceTemp - (altA / 100) * 0.98;
      const parcelB = surfaceTemp - (altB / 100) * 0.98;
      const envA = lvlA.temp;
      const envB = lvlB.temp;
      // Linear interpolation to find exact crossing altitude
      const dParcel = parcelB - parcelA;
      const dEnv = envB - envA;
      const denom = dParcel - dEnv;
      if (Math.abs(denom) > 0.001) {
        const frac = (envA - parcelA) / denom;
        const crossAlt = altA + frac * (altB - altA);
        thermalTopAlt = Math.max(0, Math.round(crossAlt));
        // Interpolate pressure
        thermalTopP = lvlA.pressure + frac * (lvlB.pressure - lvlA.pressure);
      } else {
        thermalTopAlt = Math.max(0, lvlA.altitude - surfaceAlt);
        thermalTopP = lvlA.pressure;
      }
    } else {
      thermalTopAlt = Math.max(0, lvlA.altitude - surfaceAlt);
      thermalTopP = lvlA.pressure;
    }
  }

  thermalTopAlt = analysis.thermalTopAGL;
  thermalTopP = analysis.thermalTopPressure;

  // Only mark "StabilitÃ© basse" if the thermal ceiling is genuinely below 300m
  // AND the LCL is also below 500m (if clouds exist at 500m+, air is unstable)
  const thermalTopIsLow = analysis.status === 'not-triggered' || analysis.status === 'blocked' || thermalTopAlt < 500;

  // Trigger temperature (surface temp needed to start thermals)
  const triggerTemp = surfaceTemp + (surfaceTemp - surfaceDew) * 0.1 + 1.5;

  // â”€â”€ Background lines â”€â”€
  const isobars = [1000, 925, 850, 700, 600, 500, 400, 300, 200];
  const dryAdiabatThetas = [280, 290, 300, 310, 320, 330, 340, 350, 360]; // Kelvin
  const satAdiabatTemps = [-10, 0, 10, 20, 30]; // Â°C at surface
  const humidityRatios = [0.5, 1, 2, 4, 7, 10, 16]; // g/kg

  // â”€â”€ Temperature profile path â”€â”€
  const tempProfilePts = sounding.map((lvl) => ({
    x: tToX(lvl.temp, lvl.pressure),
    y: pToY(lvl.pressure),
  }));

  // â”€â”€ Dewpoint profile path â”€â”€
  const dewProfilePts = sounding.map((lvl) => ({
    x: tToX(lvl.dewpoint, lvl.pressure),
    y: pToY(lvl.pressure),
  }));

  // â”€â”€ DALR from surface: extends from surface level upward through all sounding levels â”€â”€
  // Uses DALR = 9.8Â°C/km (0.98Â°C/100m) starting from surfaceTemp at surfaceAlt
  const dalrPts = sounding
    .filter((lvl) => lvl.altitude >= surfaceAlt)
    .map((lvl) => ({
      x: tToX(surfaceTemp - ((lvl.altitude - surfaceAlt) / 100) * 0.98, lvl.pressure),
      y: pToY(lvl.pressure),
    }));

  // â”€â”€ Thermal bubble annotation â”€â”€
  const bubbleX = tToX(triggerTemp, 1013);
  const bubbleY = pToY(1013);

  // â”€â”€ Inversion detection: find level where temp increases with altitude â”€â”€
  let inversionP: number | null = analysis.inversionBaseAGL !== null
    ? sounding.find((s) => s.altitude - surfaceAlt >= (analysis.inversionBaseAGL ?? 0))?.pressure ?? null
    : null;
  let inversionEndP: number | null = analysis.inversionTopAGL !== null
    ? sounding.find((s) => s.altitude - surfaceAlt >= (analysis.inversionTopAGL ?? 0))?.pressure ?? inversionP
    : null;
  if (!inversionP) {
    for (let i = 1; i < sounding.length - 1; i++) {
      if (sounding[i].temp > sounding[i - 1].temp) {
        inversionP = sounding[i - 1].pressure;
        inversionEndP = sounding[i + 1]?.pressure ?? sounding[i].pressure;
        break;
      }
    }
  }

  // â”€â”€ Altitude labels on right axis â”€â”€
  const maxSoundingAlt = Math.max(4000, ...sounding.map((lvl) => lvl.altitude - surfaceAlt));
  const altitudeMarkers = Array.from(
    { length: Math.min(8, Math.floor(maxSoundingAlt / 1000)) },
    (_, i) => {
      const alt = (i + 1) * 1000;
      return { alt, label: `${alt}m AMSL` };
    }
  );

  // Temperature axis labels (at bottom, skewed)
  const tempTicks = [-30, -20, -10, 0, 10, 20, 30];

  return (
    <div className="w-full overflow-x-auto">
      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-3 text-xs px-1">
        <span className="flex items-center gap-1.5">
          <svg width="24" height="8"><line x1="0" y1="4" x2="24" y2="4" stroke="#cc2200" strokeWidth="2.5"/></svg>
          <span style={{ color: 'var(--text-muted)' }}>TempÃ©rature atmosphÃ©rique</span>
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="24" height="8"><line x1="0" y1="4" x2="24" y2="4" stroke="#3b82f6" strokeWidth="2" strokeDasharray="5,3"/></svg>
          <span style={{ color: 'var(--text-muted)' }}>Point de rosÃ©e</span>
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="24" height="8"><line x1="0" y1="4" x2="24" y2="4" stroke="#1f9d55" strokeWidth="1.5" strokeDasharray="3,2" opacity="0.7"/></svg>
          <span style={{ color: 'var(--text-muted)' }}>Adiabates sÃ¨ches (Î¸)</span>
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="24" height="8"><line x1="0" y1="4" x2="24" y2="4" stroke="#16a34a" strokeWidth="1.5" strokeDasharray="6,3" opacity="0.6"/></svg>
          <span style={{ color: 'var(--text-muted)' }}>Adiabates saturÃ©es</span>
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="24" height="8"><line x1="0" y1="4" x2="24" y2="4" stroke="#65a30d" strokeWidth="1" strokeDasharray="2,3" opacity="0.5"/></svg>
          <span style={{ color: 'var(--text-muted)' }}>HumiditÃ© spÃ©cifique</span>
        </span>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ minHeight: 620, maxHeight: 760, background: '#f8f6f0', borderRadius: 8, border: '1px solid #d0c8b8' }}
      >
        {/* â”€â”€ Clip path â”€â”€ */}
        <defs>
          <clipPath id="plot-area">
            <rect x={MARGIN.left} y={MARGIN.top} width={PLOT_W} height={PLOT_H} />
          </clipPath>
        </defs>

        {/* â”€â”€ White plot background â”€â”€ */}
        <rect x={MARGIN.left} y={MARGIN.top} width={PLOT_W} height={PLOT_H} fill="white" />

        {/* â”€â”€ Specific humidity lines (dotted blue, faint) â”€â”€ */}
        <g clipPath="url(#plot-area)" opacity="0.45">
          {humidityRatios.map((w) => {
            const pts = humidityLinePoints(w);
            const path = pointsToPath(pts);
            if (!path) return null;
            const lastPt = pts[pts.length - 1];
            return (
              <g key={`hum-${w}`}>
                <path d={path} fill="none" stroke="#65a30d" strokeWidth="0.8" strokeDasharray="2,3" />
                {lastPt && lastPt.y > MARGIN.top + 5 && lastPt.y < MARGIN.top + PLOT_H - 5 && (
                  <text x={lastPt.x + 2} y={lastPt.y} fontSize="7" fill="#4d7c0f" fontFamily="serif">{w}</text>
                )}
              </g>
            );
          })}
        </g>

        {/* â”€â”€ Saturated adiabats (dashed blue) â”€â”€ */}
        <g clipPath="url(#plot-area)" opacity="0.55">
          {satAdiabatTemps.map((t) => {
            const pts = saturatedAdiabatPoints(t);
            const path = pointsToPath(pts);
            if (!path) return null;
            return (
              <path key={`sat-${t}`} d={path} fill="none" stroke="#16a34a" strokeWidth="1.2" strokeDasharray="6,3" />
            );
          })}
        </g>

        {/* â”€â”€ Dry adiabats (red diagonal lines) â”€â”€ */}
        <g clipPath="url(#plot-area)" opacity="0.6">
          {dryAdiabatThetas.map((theta) => {
            const pts = dryAdiabatPoints(theta);
            const path = pointsToPath(pts);
            if (!path) return null;
            const midPt = pts[Math.floor(pts.length / 2)];
            const tCelsius = theta - 273.15;
            return (
              <g key={`dalr-${theta}`}>
                <path d={path} fill="none" stroke="#1f9d55" strokeWidth="1" />
                {midPt && (
                  <text
                    x={midPt.x - 4}
                    y={midPt.y - 3}
                    fontSize="7.5"
                    fill="#166534"
                    fontFamily="serif"
                    transform={`rotate(-55, ${midPt.x - 4}, ${midPt.y - 3})`}
                  >
                    Î¸={Math.round(tCelsius)}
                  </text>
                )}
              </g>
            );
          })}
        </g>

        {/* â”€â”€ Isobars (horizontal dashed lines) â”€â”€ */}
        {isobars.map((p) => {
          const y = pToY(p);
          return (
            <g key={`iso-${p}`}>
              <line
                x1={MARGIN.left}
                y1={y}
                x2={MARGIN.left + PLOT_W}
                y2={y}
                stroke="#888"
                strokeWidth={p === 1000 ? 1.5 : 0.8}
                strokeDasharray={p === 1000 ? '0' : '4,3'}
              />
              {/* Pressure label left */}
              <text x={MARGIN.left - 6} y={y + 4} fontSize="9" fill="#555" textAnchor="end" fontFamily="monospace">
                {p} hPa
              </text>
            </g>
          );
        })}

        {/* â”€â”€ Inversion layer highlight â”€â”€ */}
        {inversionP && inversionEndP && (
          <rect
            x={MARGIN.left}
            y={pToY(inversionP)}
            width={PLOT_W}
            height={Math.abs(pToY(inversionEndP) - pToY(inversionP))}
            fill="rgba(251,191,36,0.18)"
            clipPath="url(#plot-area)"
          />
        )}

        {/* â”€â”€ LCL / Cumulus base line â”€â”€ */}
        <line
          x1={MARGIN.left}
          y1={pToY(lclPressure)}
          x2={MARGIN.left + PLOT_W}
          y2={pToY(lclPressure)}
          stroke="#00d4ff"
          strokeWidth="1.5"
          strokeDasharray="5,3"
          clipPath="url(#plot-area)"
        />

        {/* â”€â”€ Thermal top line â”€â”€ */}
        {showThermalTop && thermalTopP > 0 && (
          <line
            x1={MARGIN.left}
            y1={pToY(thermalTopP)}
            x2={MARGIN.left + PLOT_W}
            y2={pToY(thermalTopP)}
            stroke="#a78bfa"
            strokeWidth="1.5"
            strokeDasharray="6,3"
            clipPath="url(#plot-area)"
          />
        )}

        {/* â”€â”€ DALR from surface (yellow) â”€â”€ */}
        {showDalr && (
          <path
            d={pointsToPath(dalrPts)}
            fill="none"
            stroke="#f59e0b"
            strokeWidth="1.8"
            strokeDasharray="5,3"
            clipPath="url(#plot-area)"
            opacity="0.85"
          />
        )}

        {/* â”€â”€ Dewpoint profile (blue) â”€â”€ */}
        {showDewpoint && (
          <path
            d={pointsToPath(dewProfilePts)}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2.5"
            clipPath="url(#plot-area)"
          />
        )}

        {/* â”€â”€ Temperature profile (dark red) â”€â”€ */}
        <path
          d={pointsToPath(tempProfilePts)}
          fill="none"
          stroke="#8b0000"
          strokeWidth="3"
          clipPath="url(#plot-area)"
        />

        {/* â”€â”€ Sounding dots â”€â”€ */}
        {sounding.map((lvl, i) => (
          <g key={`dot-${i}`} clipPath="url(#plot-area)">
            <circle cx={tToX(lvl.temp, lvl.pressure)} cy={pToY(lvl.pressure)} r="3" fill="#8b0000" />
            {showDewpoint && (
              <circle cx={tToX(lvl.dewpoint, lvl.pressure)} cy={pToY(lvl.pressure)} r="2.5" fill="#3b82f6" />
            )}
          </g>
        ))}

        {/* â”€â”€ Thermal bubble annotation â”€â”€ */}
        <g>
          <circle cx={bubbleX} cy={bubbleY - 6} r="7" fill="#cc2200" opacity="0.9" />
          <rect
            x={bubbleX + 10}
            y={bubbleY - 28}
            width={130}
            height={36}
            rx="4"
            fill="white"
            stroke="#333"
            strokeWidth="0.8"
          />
          <text x={bubbleX + 17} y={bubbleY - 14} fontSize="9.5" fill="#cc2200" fontWeight="bold" fontFamily="sans-serif">
            Bulle d&apos;air Ã  {Math.round(triggerTemp)}Â°
          </text>
          <text x={bubbleX + 17} y={bubbleY - 3} fontSize="8" fill="#555" fontFamily="sans-serif">
            Temp. dÃ©clenchement
          </text>
          {/* Arrow */}
          <line x1={bubbleX + 10} y1={bubbleY - 10} x2={bubbleX + 3} y2={bubbleY - 6} stroke="#333" strokeWidth="1" markerEnd="url(#arrow)" />
        </g>

        {/* â”€â”€ Inversion annotation â”€â”€ */}
        {inversionP && (
          <g>
            <rect
              x={MARGIN.left + PLOT_W * 0.28}
              y={pToY(inversionP) - 38}
              width={175}
              height={46}
              rx="4"
              fill="white"
              stroke="#333"
              strokeWidth="0.8"
            />
            <text x={MARGIN.left + PLOT_W * 0.28 + 8} y={pToY(inversionP) - 22} fontSize="9" fill="#333" fontWeight="bold" fontFamily="sans-serif">
              Inversion de tempÃ©rature
            </text>
            <text x={MARGIN.left + PLOT_W * 0.28 + 8} y={pToY(inversionP) - 11} fontSize="8.5" fill="#555" fontFamily="sans-serif">
              qui bloque les ascendances
            </text>
            <line
              x1={MARGIN.left + PLOT_W * 0.28 + 87}
              y1={pToY(inversionP) + 8}
              x2={MARGIN.left + PLOT_W * 0.28 + 87}
              y2={pToY(inversionP) - 0}
              stroke="#333"
              strokeWidth="1"
            />
          </g>
        )}

        {/* â”€â”€ Arrow marker def â”€â”€ */}
        <defs>
          <marker id="arrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
            <path d="M0,0 L0,6 L6,3 z" fill="#333" />
          </marker>
          <marker id="arrow-black" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
            <path d="M0,0 L0,8 L8,4 z" fill="#111" />
          </marker>
        </defs>

        {/* â”€â”€ Altitude markers on right side â”€â”€ */}
        {altitudeMarkers.map(({ alt, label }) => {
          // Convert altitude to approximate pressure
          let p = 1013.25 * Math.pow(1 - alt / 44330, 5.255);
          const y = pToY(p);
          const markerAGL = alt - surfaceAlt;
          const isLcl = Math.abs(markerAGL - lclAlt) < 200;
          const isThermalTop = showThermalTop && Math.abs(markerAGL - thermalTopAlt) < 200;
          return (
            <g key={`alt-${alt}`}>
              <line
                x1={MARGIN.left + PLOT_W + 2}
                y1={y}
                x2={MARGIN.left + PLOT_W + 8}
                y2={y}
                stroke={isLcl ? '#00d4ff' : isThermalTop ? '#a78bfa' : '#555'}
                strokeWidth="1.2"
              />
              <text
                x={MARGIN.left + PLOT_W + 12}
                y={y + 4}
                fontSize="9.5"
                fill={isLcl ? '#00d4ff' : isThermalTop ? '#a78bfa' : '#444'}
                fontFamily="sans-serif"
                fontWeight={isLcl || isThermalTop ? 'bold' : 'normal'}
              >
                {label}
              </text>
            </g>
          );
        })}

        {/* â”€â”€ Plafond (LCL) arrow annotation â”€â”€ */}
        <g>
          <line
            x1={MARGIN.left + PLOT_W - 10}
            y1={pToY(lclPressure)}
            x2={MARGIN.left + PLOT_W + 8}
            y2={pToY(lclPressure)}
            stroke="#00d4ff"
            strokeWidth="2"
            markerEnd="url(#arrow-black)"
          />
          <text
            x={MARGIN.left + PLOT_W + 12}
            y={pToY(lclPressure) - 6}
            fontSize="9"
            fill="#00d4ff"
            fontWeight="bold"
            fontFamily="sans-serif"
          >
            LCL theorique
          </text>
          <text
            x={MARGIN.left + PLOT_W + 12}
            y={pToY(lclPressure) + 5}
            fontSize="8"
            fill="#00d4ff"
            fontFamily="sans-serif"
          >
            {lclAlt}m
          </text>
        </g>

        {/* â”€â”€ Thermal top annotation â”€â”€ */}
        {showThermalTop && thermalTopP > 0 && (
          <g>
            <text
              x={MARGIN.left + PLOT_W + 12}
              y={pToY(thermalTopP) - 4}
              fontSize="8.5"
              fill="#a78bfa"
              fontWeight="bold"
              fontFamily="sans-serif"
            >
              Sommet
            </text>
            <text
              x={MARGIN.left + PLOT_W + 12}
              y={pToY(thermalTopP) + 6}
              fontSize="8"
              fill="#a78bfa"
              fontFamily="sans-serif"
            >
              thermique
            </text>
            <text
              x={MARGIN.left + PLOT_W + 12}
              y={pToY(thermalTopP) + 16}
              fontSize="8"
              fill="#a78bfa"
              fontFamily="sans-serif"
            >
              {thermalTopAlt}m
            </text>
          </g>
        )}

        {/* â”€â”€ Temperature axis (bottom, skewed labels) â”€â”€ */}
        {tempTicks.map((t) => {
          const x = tToX(t, P_BOT);
          const yBot = MARGIN.top + PLOT_H;
          return (
            <g key={`ttick-${t}`}>
              <line x1={x} y1={yBot} x2={x} y2={yBot + 5} stroke="#555" strokeWidth="0.8" />
              <text x={x} y={yBot + 15} fontSize="9" fill="#555" textAnchor="middle" fontFamily="monospace">
                {t}
              </text>
            </g>
          );
        })}

        {/* â”€â”€ Axis labels â”€â”€ */}
        <text x={MARGIN.left + PLOT_W / 2} y={H - 4} fontSize="10" fill="#444" textAnchor="middle" fontFamily="sans-serif">
          TempÃ©rature (Â°C)
        </text>
        <text
          x={14}
          y={MARGIN.top + PLOT_H / 2}
          fontSize="10"
          fill="#444"
          textAnchor="middle"
          fontFamily="sans-serif"
          transform={`rotate(-90, 14, ${MARGIN.top + PLOT_H / 2})`}
        >
          Pression (hPa)
        </text>

        {/* â”€â”€ Bottom legend â”€â”€ */}
        <text x={MARGIN.left} y={H - 4} fontSize="8" fill="#777" fontFamily="sans-serif">
          qs (g/kg)
        </text>
        <g transform={`translate(${MARGIN.left + 40}, ${H - 6})`}>
          <line x1="0" y1="0" x2="14" y2="0" stroke="#888" strokeWidth="0.8" strokeDasharray="3,2" />
          <text x="17" y="3" fontSize="7.5" fill="#666" fontFamily="sans-serif">Isobares (pression)</text>
        </g>
        <g transform={`translate(${MARGIN.left + 155}, ${H - 6})`}>
          <line x1="0" y1="0" x2="14" y2="0" stroke="#16a34a" strokeWidth="1.2" strokeDasharray="5,3" />
          <text x="17" y="3" fontSize="7.5" fill="#666" fontFamily="sans-serif">adiabates saturÃ©es</text>
        </g>
        <g transform={`translate(${MARGIN.left + 285}, ${H - 6})`}>
          <line x1="0" y1="0" x2="14" y2="0" stroke="#1f9d55" strokeWidth="1" />
          <text x="17" y="3" fontSize="7.5" fill="#666" fontFamily="sans-serif">dry adiabats (Î¸)</text>
        </g>

        {/* â”€â”€ Border â”€â”€ */}
        <rect
          x={MARGIN.left}
          y={MARGIN.top}
          width={PLOT_W}
          height={PLOT_H}
          fill="none"
          stroke="#888"
          strokeWidth="1"
        />
      </svg>

      {/* â”€â”€ Key values summary below chart â”€â”€ */}
      <div className="flex flex-wrap gap-3 mt-3 text-xs">
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(139,0,0,0.12)', border: '1px solid rgba(139,0,0,0.3)' }}>
          <span style={{ color: 'var(--text-muted)' }}>Temp. sol:</span>
          <span className="font-mono font-bold" style={{ color: '#f87171' }}>{surfaceTemp.toFixed(1)}Â°C</span>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)' }}>
          <span style={{ color: 'var(--text-muted)' }}>RosÃ©e sol:</span>
          <span className="font-mono font-bold" style={{ color: '#60a5fa' }}>{surfaceDew.toFixed(1)}Â°C</span>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)' }}>
          <span style={{ color: 'var(--text-muted)' }}>Base Cu theorique (LCL):</span>
          <span className="font-mono font-bold" style={{ color: '#00d4ff' }}>{lclAlt}m</span>
        </div>
        {lclAlt > thermalTopAlt && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)' }}>
            <span className="font-mono font-bold" style={{ color: '#fbbf24' }}>LCL non atteint par la bulle</span>
          </div>
        )}
        {showThermalTop && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.3)' }}>
            <span style={{ color: 'var(--text-muted)' }}>Potentiel thermique:</span>
            {thermalTopIsLow ? (
              <span className="font-mono font-bold" style={{ color: '#fbbf24' }}>Trop stable / plafond bas</span>
            ) : (
              <span className="font-mono font-bold" style={{ color: '#a78bfa' }}>{thermalTopAlt}m ({Math.round(thermalTopAlt * 3.281)}ft)</span>
            )}
          </div>
        )}
        {inversionP && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)' }}>
            <span style={{ color: 'var(--text-muted)' }}>Inversion:</span>
            <span className="font-mono font-bold" style={{ color: '#fbbf24' }}>{pToAlt(inversionP)}m ({inversionP}mb)</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Trigger temp is now computed from real sounding data in the component
